import { jsxs, jsx } from 'react/jsx-runtime';
import { useContext, useMemo } from 'react';
import { AppContext } from '../../../context/context.esm.js';
import HelpPanel from '../Panels/HelpPanel/HelpPanel.esm.js';
import { PRIVATE_PLANS_LOCATION_TYPE, FAVORITES_LOCATION_TYPE } from './constants.esm.js';
import UserPanel from '../Panels/UserPanel/UserPanel.esm.js';
import FeedbackPanel from '../Panels/FeedbackPanel/FeedbackPanel.esm.js';
import LanguageAndRegionPanel from '../Panels/LanguageAndRegionPanel/LanguageAndRegionPanel.esm.js';
import { Shimmer, ShimmerElementType, Stack, Text, FontWeights } from '@fluentui/react';
import { useTranslation } from 'react-i18next';
import { useHydration } from '@agoras/hooks';
import { useUserQuery } from '../../../../hooks/useUserQuery.esm.js';
import { useLocale } from '../../../../hooks/useLocale.esm.js';
import { useMarket } from '../../../../hooks/useMarket.esm.js';

var useHeaderButtons = function() {
    var _ref = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {}, _ref_excludeKeys = _ref.excludeKeys, excludeKeys = _ref_excludeKeys === void 0 ? [] : _ref_excludeKeys;
    var _useContext = useContext(AppContext), _useContext_user = _useContext.user, signedIn = _useContext_user.signedIn, loading = _useContext_user.loading, openSignInModal = _useContext.openSignInModal, navigate = _useContext.navigate;
    var locale = useLocale().locale;
    var market = useMarket().market;
    var favoritesCount = useUserQuery().favoritesCount;
    var t = useTranslation('header').t;
    var isHydrated = useHydration();
    var feedbackText = t('Give feedback');
    var helpText = t('Help + support');
    var languageAndBillingFullText = t('Billing country and language');
    var privatePlansText = t('Private plans');
    var favoritesButtonAriaLabel = t('Saved items');
    var myAccountText = t('My account');
    var headerButtons = useMemo(function() {
        return [
            {
                key: 'user',
                text: myAccountText,
                iconName: 'Contact',
                ariaLabel: myAccountText,
                tooltip: myAccountText,
                onClick: function() {
                    if (!signedIn) {
                        openSignInModal === null || openSignInModal === void 0 ? void 0 : openSignInModal();
                    }
                },
                disabled: loading,
                relatedPanel: signedIn ? UserPanel : undefined,
                fullText: myAccountText
            },
            {
                key: 'languageAndRegion',
                text: "".concat(market.name, " | ").concat(locale.language.toUpperCase()),
                iconName: 'World',
                ariaLabel: languageAndBillingFullText,
                tooltip: isHydrated ? /*#__PURE__*/ jsxs(Stack, {
                    horizontalAlign: "center",
                    tokens: {
                        childrenGap: 4
                    },
                    children: [
                        /*#__PURE__*/ jsx(Text, {
                            variant: "smallPlus",
                            styles: {
                                root: {
                                    fontWeight: FontWeights.bold
                                }
                            },
                            children: languageAndBillingFullText
                        }),
                        /*#__PURE__*/ jsxs(Text, {
                            variant: "smallPlus",
                            children: [
                                market.name,
                                ", ",
                                locale.name
                            ]
                        })
                    ]
                }) : null,
                disabled: false,
                relatedPanel: LanguageAndRegionPanel,
                fullText: languageAndBillingFullText,
                iconButton: false,
                onRenderText: function() {
                    return /*#__PURE__*/ jsx(Shimmer, {
                        isDataLoaded: isHydrated,
                        shimmerElements: [
                            {
                                type: ShimmerElementType.line,
                                width: '100px',
                                height: 20
                            }
                        ],
                        children: isHydrated && /*#__PURE__*/ jsx(Stack, {
                            horizontal: true,
                            tokens: {
                                childrenGap: 8
                            },
                            children: /*#__PURE__*/ jsx(Text, {
                                children: locale.language.toUpperCase()
                            })
                        })
                    });
                }
            },
            {
                key: 'privatePlans',
                text: privatePlansText,
                iconName: 'Lock',
                ariaLabel: privatePlansText,
                tooltip: privatePlansText,
                onClick: function() {
                    if (!signedIn) {
                        openSignInModal === null || openSignInModal === void 0 ? void 0 : openSignInModal();
                    } else {
                        navigate({
                            type: PRIVATE_PLANS_LOCATION_TYPE
                        });
                    }
                },
                disabled: loading,
                fullText: privatePlansText
            },
            {
                key: 'favorites',
                text: favoritesButtonAriaLabel,
                iconName: 'FavoriteStar',
                ariaLabel: favoritesButtonAriaLabel,
                tooltip: signedIn ? t('You saved to your list', {
                    favoritesCount: favoritesCount
                }) : favoritesButtonAriaLabel,
                onClick: function() {
                    if (!signedIn) {
                        openSignInModal === null || openSignInModal === void 0 ? void 0 : openSignInModal();
                    } else {
                        navigate({
                            type: FAVORITES_LOCATION_TYPE
                        });
                    }
                },
                disabled: loading,
                fullText: favoritesButtonAriaLabel,
                iconCounter: favoritesCount
            },
            {
                key: 'help',
                text: helpText,
                iconName: 'Help',
                relatedPanel: HelpPanel,
                disabled: false,
                ariaLabel: helpText,
                tooltip: helpText,
                fullText: helpText,
                iconButton: true
            },
            {
                key: 'feedback',
                text: feedbackText,
                iconName: 'Emoji2',
                relatedPanel: FeedbackPanel,
                disabled: false,
                ariaLabel: feedbackText,
                tooltip: feedbackText,
                fullText: feedbackText
            }
        ].filter(function(param) {
            var key = param.key;
            return !excludeKeys.includes(key);
        });
    }, [
        t,
        myAccountText,
        loading,
        signedIn,
        market,
        locale,
        languageAndBillingFullText,
        privatePlansText,
        favoritesButtonAriaLabel,
        favoritesCount,
        helpText,
        feedbackText,
        openSignInModal,
        navigate,
        excludeKeys,
        isHydrated
    ]);
    return [
        headerButtons
    ];
};

export { useHeaderButtons };
